/**
 * Dukakit Activity Tracker
 *
 * Provides two ways to track events from any component:
 *
 *   1. Programmatic:  this.$track('checkout_started', { order_id: id }, 'checkout')
 *   2. Directive:     <button v-track="'report_opened'">
 *                     <button v-track="{ name: 'item_added_to_cart', type: 'pos', meta: { id } }">
 *
 * Fire-and-forget — errors are swallowed so tracking never breaks the UI.
 */

const TrackerPlugin = {
    install(Vue) {
        /**
         * @param {string}  eventName   e.g. 'checkout_started'
         * @param {object}  metadata    arbitrary key/value payload
         * @param {string}  eventType   category: 'pos' | 'checkout' | 'navigation' | 'inventory' | 'customer' | 'report'
         * @param {string}  entityType  e.g. 'product', 'order', 'customer'
         * @param {string}  entityId    UUID of the linked entity
         */
        const track = (eventName, metadata = {}, eventType = 'navigation', entityType = null, entityId = null) => {
            if (!eventName) return;

            window.axios
                .post('/api/track', {
                    event_type:  eventType,
                    event_name:  eventName,
                    route:       window.location.pathname + window.location.search,
                    entity_type: entityType,
                    entity_id:   entityId,
                    metadata:    {
                        ...metadata,
                        page_title: document.title,
                    },
                })
                .catch(() => {}); // silently swallow — callers must never await this
        };

        Vue.prototype.$track = track;

        /**
         * v-track directive — attaches a click listener automatically.
         *
         * Simple:   v-track="'report_opened'"
         * Full:     v-track="{ name: 'item_added_to_cart', type: 'pos', meta: { productId: 1 }, entityType: 'product', entityId: '...' }"
         */
        Vue.directive('track', {
            bind(el, binding) {
                const value = binding.value;

                let eventName, metadata, eventType, entityType, entityId;

                if (typeof value === 'string') {
                    eventName  = value;
                    metadata   = {};
                    eventType  = 'navigation';
                    entityType = null;
                    entityId   = null;
                } else if (value && typeof value === 'object') {
                    eventName  = value.name;
                    metadata   = value.meta   || {};
                    eventType  = value.type   || 'navigation';
                    entityType = value.entityType || null;
                    entityId   = value.entityId   || null;
                } else {
                    return;
                }

                el.__trackHandler__ = () => track(eventName, metadata, eventType, entityType, entityId);
                el.addEventListener('click', el.__trackHandler__);
            },

            unbind(el) {
                if (el.__trackHandler__) {
                    el.removeEventListener('click', el.__trackHandler__);
                    delete el.__trackHandler__;
                }
            },
        });
    },
};

export default TrackerPlugin;
